const isString = (value: unknown): value is string => typeof value === 'string';

const safeJSONParse = <T>(value: string, fallback: T): T => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/**
 * Utility for handling sessionStorage with type safety and error handling
 */

export const sessionStorageUtils = {
  /**
   * Get item from sessionStorage with type safety
   * @param key Storage key
   * @param defaultValue Default value if key not found or parsing fails
   * @returns Parsed data or default value
   */
  getItem<T>(key: string, defaultValue: T): T {
    if (typeof window === 'undefined') {
      return defaultValue;
    }

    try {
      const item = sessionStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }

      if (isString(defaultValue)) {
        return (item as T) || defaultValue;
      }

      return safeJSONParse(item, defaultValue);
    } catch {
      return defaultValue;
    }
  },

  /**
   * Set item in sessionStorage with type safety
   * @param key Storage key
   * @param value Value to store
   */
  setItem<T>(key: string, value: T): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const serializedValue = isString(value) ? value : JSON.stringify(value);
      sessionStorage.setItem(key, serializedValue);
    } catch {
      /* Silently fail - storage may be full or disabled */
    }
  },

  /**
   * Remove item from sessionStorage
   * @param key Storage key
   */
  removeItem(key: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      sessionStorage.removeItem(key);
    } catch {
      /* Silently fail - storage may be disabled */
    }
  },
};
